import { z } from 'zod';
import { PinComponentSchema } from '../pin-component';
import { LayoutPartSchema } from '../pin-component/layout-part';
import { TipTapContentSchema } from '../tiptap-content.schema';
import { BaseInteractiveQuestionPinSettingsSchema, BasePinSchema, BaseTemplateLayoutSchema } from './base-pin';
import { AnswerSettingSchema } from './base-student-pin';
import { BaseTeacherPinSettingsSchema } from './base-teacher-pin';

const QuizPinAnswerImageSchema = z.object({
  type: z.literal('IMAGE'),
  id: z.string().min(1),
  url: z.string().min(1).optional(),
});
export type QuizPinAnswerImage = z.infer<typeof QuizPinAnswerImageSchema>;
const QuizPinAnswerTextSchema = z.object({
  type: z.literal('TEXT'),
  id: z.string().min(1),
  text: TipTapContentSchema,
});
export type QuizPinAnswerText = z.infer<typeof QuizPinAnswerTextSchema>;
const QuizPinAnswerSchema = z.discriminatedUnion('type', [QuizPinAnswerImageSchema, QuizPinAnswerTextSchema]);
export type QuizPinAnswer = z.infer<typeof QuizPinAnswerSchema>;

const QuizPinLayoutKeySchema = z.enum(['TITLE_ONLY', 'IMAGE_LEFT', 'IMAGE_RIGHT', 'IMAGE_ONLY', 'EMPTY']);
export type QuizPinLayoutKey = z.infer<typeof QuizPinLayoutKeySchema>;

export const QuizLayoutPartKeySchema = LayoutPartSchema.extract(['TITLE', 'IMAGE1']);

export const QuizPinLayoutTemplateSchema = BaseTemplateLayoutSchema.extend(
  z.object({
    key: QuizPinLayoutKeySchema,
    layoutParts: z.partialRecord(QuizLayoutPartKeySchema, PinComponentSchema.optional()),
  }).shape
);
export type QuizPinLayoutTemplate = z.infer<typeof QuizPinLayoutTemplateSchema>;

export const QuizPinSettingsSchema = BaseInteractiveQuestionPinSettingsSchema.extend(
  z.object({
    answers: z.array(QuizPinAnswerSchema),
    layout: QuizPinLayoutKeySchema,
    layoutParts: z.partialRecord(QuizLayoutPartKeySchema, z.string().optional()),
  }).shape
);

export type QuizPinSettings = z.infer<typeof QuizPinSettingsSchema>;

export const QuizTeacherPinSettingsSchema = BaseTeacherPinSettingsSchema.extend(
  z.object({
    correctAnswers: z.array(z.string()),
    // TODO: Unused?
    shuffleMap: z.any().optional(), // shuffling answers (assessment (test) only)
  }).shape
);

export type QuizTeacherPinSettings = z.infer<typeof QuizTeacherPinSettingsSchema>;

export const QuizStudentPinSettingsSchema = AnswerSettingSchema;

export type QuizStudentPinSettings = z.infer<typeof QuizStudentPinSettingsSchema>;

export const QuizPinTypeSchema = z.literal('MULTIPLE_CHOICE');

export const QuizPinSchema = BasePinSchema.extend(
  z.object({
    type: QuizPinTypeSchema,
    settings: QuizPinSettingsSchema,
  }).shape
);

export type QuizPin = z.infer<typeof QuizPinSchema>;

export const MINIMAL_QUIZ_ANSWERS = 2;
export const MAXIMAL_QUIZ_ANSWERS = 4;
